import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Autocomplete, Box, TextField, Typography } from '@mui/material';
import useGetListGroupByNameDockerImage from 'redux/slices/dockerImage/hooks/useGetListGroupByNameDockerImage';

// Wizard step 1 — pick the build-source ImageName from the existing Docker Image data
// (AC-5). The source of "existing Docker Image data" is Wave 1's own
// GET /api/docker-images/group-by-name (already used by the Docker Image list page).
const SourceImageStep = ({ values, errors, touched, setFieldValue }) => {
  const { data, isLoading } = useGetListGroupByNameDockerImage();
  // GET /api/docker-images/group-by-name returns the grouped list as a plain array on
  // `data.data` (each item keyed by `ImageName`), NOT a paged `{ Items }` envelope — same
  // shape DockerImageGroupByNameSelect consumes. Reading `.Items` here made the list always
  // appear empty ("No Docker images are registered yet") even when images exist.
  const images = data?.data ?? [];

  if (!isLoading && images.length === 0) {
    return (
      <Alert severity='info'>
        No Docker images are registered yet. Add and build a Docker image first, then
        return here to create a Pipeline.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography component='p' color='text.primary' fontSize={14} mb={2}>
        Source image
        <Box component='span' ml={1} sx={{ color: 'error.main' }}>
          *
        </Box>
      </Typography>
      <Autocomplete
        fullWidth
        loading={isLoading}
        options={images}
        getOptionLabel={(option) => option?.ImageName ?? ''}
        isOptionEqualToValue={(option, value) => option?.ImageName === value?.ImageName}
        value={images.find((i) => i.ImageName === values.ImageName) ?? null}
        onChange={(_e, option) => setFieldValue('ImageName', option?.ImageName ?? '')}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder='Select a Docker image'
            error={touched?.ImageName && !!errors?.ImageName}
            helperText={touched?.ImageName ? errors?.ImageName : ''}
            inputProps={{ ...params.inputProps, 'aria-label': 'Source image' }}
          />
        )}
      />
    </Box>
  );
};

export default React.memo(SourceImageStep);

SourceImageStep.propTypes = {
  values: PropTypes.object,
  errors: PropTypes.object,
  touched: PropTypes.object,
  setFieldValue: PropTypes.func,
};
